import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { I18n } from 'react-redux-i18n';
import { connect } from 'react-redux';
import ModernDatepicker from 'react-modern-datepicker';

import { createPack, updatePack, uploadPackImage } from '../../app/store/actions/packs';
import PacksRequests from '../../app/server/packs';
import fields from '../../data/fields';
import NumbersHelper from '../../helpers/numbers';

let productHelper = null;

class PackForm extends Component {
  constructor(props) {
    super(props);

    this.inputRef = React.createRef();
    this.searchDebounceTimer = null;

    this.state = {
      id: null,
      editPack: false,
      isLoading: false,
      isLoadingProducts: false,
      name: '',
      description: '',
      dateEffective: '',
      dateExpiration: '',
      status: '',
      messageError: '',
      messageSuccess: '',
      products: [],
      searchValue: '',
      searchResults: [],
      showDropdown: false,
      pricingType: '',
      fixedPrice: '',
      discountPercentage: '',
      imageFile: null,
      imagePreview: null,
      productAdjustedPrices: {},
      suggestedPrices: {},
      roundingWarning: false,
      roundingDifference: 0,
    };
  }

  setValue = (att, value) => {
    if (value && value < 0) {
      this.setState({ [att]: Math.abs(value) });
    } else {
      this.setState({ [att]: value });
    }
  };

  parseCurrencyValue = (value) => {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const sanitized = String(value).trim().replace(/[^\d,.-]/g, '');
    if (!sanitized) return null;

    const lastComma = sanitized.lastIndexOf(',');
    const lastDot = sanitized.lastIndexOf('.');
    let normalized = sanitized;

    if (lastComma > -1 && lastDot > -1) {
      if (lastComma > lastDot) {
        normalized = sanitized.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = sanitized.replace(/,/g, '');
      }
    } else if (lastComma > -1) {
      normalized = sanitized.replace(',', '.');
    } else if (lastDot > -1) {
      const dotParts = sanitized.split('.');
      if (dotParts.length > 2 || dotParts[dotParts.length - 1].length === 3) {
        normalized = sanitized.replace(/\./g, '');
      }
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? Math.round(parsed) : null;
  };

  formatCurrencyInputValue = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const parsed = this.parseCurrencyValue(value);
    if (parsed === null) return '';
    return parsed.toLocaleString('es-CL', {
      maximumFractionDigits: 0,
    });
  };

  sanitizeCurrencyInputValue = value => String(value)
    .replace(/[^\d,.-]/g, '');

  getAdjustedUnitPrice = productId => this.parseCurrencyValue(
    this.state.productAdjustedPrices[productId],
  );

  getSuggestedUnitPrice = productId => this.state.suggestedPrices[productId] || 0;

  getFinalUnitPrice = (product) => {
    const adjustedPrice = this.getAdjustedUnitPrice(product.id);
    return adjustedPrice !== null ? adjustedPrice : this.getSuggestedUnitPrice(product.id);
  };

  goBack = () => {
    this.props.back.goBack();
  };

  createPack = async () => {
    this.setState({ isLoading: true });
    const {
      name,
      dateEffective,
      dateExpiration,
      status,
      products,
      pricingType,
      fixedPrice,
      discountPercentage,
    } = this.state;

    if (
      !name
      || !dateEffective
      || !dateExpiration
      || !status
      || products.length === 0
      || !this.isPricingValid()
    ) {
      this.setState({
        messageError: I18n.t('fieldsNotReported'),
        isLoading: false,
      });
      return;
    }

    try {
      const { imageFile } = this.state;
      const created = await this.props.createPacks({
        name,
        description: this.state.description || null,
        dateEffective,
        dateExpiration,
        status,
        priceType: pricingType,
        ...(pricingType === 'fixed' && { price: parseFloat(fixedPrice) }),
        ...(pricingType === 'percentage' && {
          price: parseFloat(discountPercentage),
        }),
        products: products.map(p => ({
          productId: p.id,
          quantity: p.qty,
          originalUnitPrice: p.originalPrice,
          adminAdjustedUnitPrice: this.parseCurrencyValue(this.state.productAdjustedPrices[p.id]),
        })),
      });

      if (imageFile && created && created.id) {
        await this.props.uploadImage(imageFile, created.id);
      }

      await this.setState({ messageSuccess: I18n.t('changeMade') });
      await this.goBack();
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({ messageSuccess: '' });

      if (error && error.message) {
        const messageError = error.code && error.details
          ? `${error.code} - ${error.details} | ${error.message}`
          : error.message;

        this.setState({ messageError });
      } else {
        this.setState({ messageError: I18n.t('errorOcurred') });
      }
    }
    this.setState({ isLoading: false });
  };

  updatePack = async () => {
    this.setState({ isLoading: true });
    const {
      id,
      name,
      dateEffective,
      dateExpiration,
      status,
      products,
      pricingType,
      fixedPrice,
      discountPercentage,
    } = this.state;

    if (
      !name
      || !dateEffective
      || !dateExpiration
      || !status
      || products.length === 0
      || !this.isPricingValid()
    ) {
      this.setState({
        messageError: I18n.t('fieldsNotReported'),
        isLoading: false,
      });
      return;
    }

    try {
      const { imageFile } = this.state;
      await this.props.updatePacks({
        id,
        name,
        description: this.state.description || null,
        dateEffective,
        dateExpiration,
        status,
        priceType: pricingType,
        ...(pricingType === 'fixed' && { price: parseFloat(fixedPrice) }),
        ...(pricingType === 'percentage' && {
          price: parseFloat(discountPercentage),
        }),
        products: products.map(p => ({
          productId: p.id,
          quantity: p.qty,
          originalUnitPrice: p.originalPrice,
          adminAdjustedUnitPrice: this.parseCurrencyValue(this.state.productAdjustedPrices[p.id]),
        })),
      });

      if (imageFile && id) {
        await this.props.uploadImage(imageFile, id);
      }

      await this.setState({ messageSuccess: I18n.t('changeMade') });
      await this.goBack();
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({ messageSuccess: '' });

      if (error && error.message) {
        const messageError = error.code && error.details
          ? `${error.code} - ${error.details} | ${error.message}`
          : error.message;

        this.setState({ messageError });
      } else {
        this.setState({ messageError: I18n.t('errorOcurred') });
      }
    }
    this.setState({ isLoading: false });
  };

  setDate = (att, value) => {
    if (value) {
      const [day, month, year] = value.split('-');
      this.setState({ [att]: `${year}-${month}-${day}` });
    } else {
      this.setState({ [att]: '' });
    }
  };

  componentDidMount = async () => {
    if (this.props.back.location.selectedPack) {
      this.setSelectedPack(this.props.back.location.selectedPack);
    }
  };

  componentWillUnmount = () => {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  };

  setSelectedPack = (detail) => {
    const toIso = (dateStr) => {
      if (!dateStr) return '';
      return String(dateStr).substring(0, 10);
    };

    const products = (detail.packsProducts || []).map(p => ({
      id: p.product ? p.product.soneparId : p.id || p.soneparId,
      code: (p.product && p.product.code) || p.code || '',
      name: (p.product && p.product.description) || p.name || p.code || '',
      qty: p.quantity || p.qty || 1,
      originalPrice: p.originalUnitPrice || 0,
      currentUnitPrice: parseFloat(p.currentUnitPrice || 0),
      hasPrice: !!p.originalUnitPrice,
    }));
    const productAdjustedPrices = {};
    (detail.packsProducts || []).forEach((p) => {
      const productId = p.product ? p.product.soneparId : p.id || p.soneparId;
      if (p.adminAdjustedUnitPrice !== undefined && p.adminAdjustedUnitPrice !== null) {
        productAdjustedPrices[productId] = this.formatCurrencyInputValue(p.adminAdjustedUnitPrice);
      }
    });

    this.setState({
      id: detail.id,
      editPack: true,
      name: detail.name || '',
      description: detail.description || '',
      dateEffective: toIso(detail.dateEffective),
      dateExpiration: toIso(detail.dateExpiration),
      status: detail.status ? 'active' : 'inactive',
      pricingType: detail.priceType || '',
      fixedPrice: detail.priceType === 'fixed' ? String(detail.price || '') : '',
      discountPercentage: detail.priceType === 'percentage' ? String(detail.price || '') : '',
      products,
      productAdjustedPrices,
      imagePreview: detail.imageUrl || null,
    }, () => {
      this.calculateSuggestedPrices();
    });
  };

  setOnChange = (value) => {
    // Update input value immediately
    this.setState({
      searchValue: value,
      messageError: '',
    });

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    if (value.length < 2) {
      this.setState({
        showDropdown: false,
        searchResults: [],
        isLoadingProducts: false,
      });
      return;
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.performSearch(value);
    }, 500);
  };

  performSearch = async (value) => {
    this.setState({ isLoadingProducts: true });
    try {
      const data = await PacksRequests.getSearchProducts(10, 0, value);
      const results = Array.isArray(data) ? data : [];
      this.setState({
        searchResults: results,
        showDropdown: results.length > 0,
        isLoadingProducts: false,
      });
    } catch (err) {
      this.setState({ searchResults: [], showDropdown: false, isLoadingProducts: false });
    }
  };

  selectProduct = (item) => {
    productHelper = item;
    this.addProduct();
  };

  addProduct = () => {
    if (!productHelper || !productHelper.soneparId) return;
    const { products } = this.state;
    const alreadyAdded = products.some(p => p.id === productHelper.soneparId);
    if (alreadyAdded) {
      this.setState({
        messageError: I18n.t('productAlreadyAdded'),
        searchValue: '',
      });
      productHelper = null;
      return;
    }
    this.setState({
      products: [
        ...products,
        {
          id: productHelper.soneparId,
          code: productHelper.code,
          name:
            productHelper.description
            || (productHelper.product && productHelper.product.descriptionLarge)
            || productHelper.code,
          qty: 1,
          originalPrice: productHelper.pricing
            ? productHelper.pricing.pricing
            : 0,
          currentUnitPrice: 0,
          hasPrice: !!(productHelper.pricing && productHelper.pricing.pricing),
        },
      ],
      searchValue: '',
      searchResults: [],
      showDropdown: false,
      messageError: '',
    }, () => {
      this.calculateSuggestedPrices();
    });
    productHelper = null;
  };

  updateQty = (id, qty) => {
    const value = Math.max(1, Number(qty) || 1);
    this.setState(prev => ({
      products: prev.products.map(p => (p.id === id ? { ...p, qty: value } : p)),
    }), () => {
      this.calculateSuggestedPrices();
    });
  };

  isPricingValid = () => {
    const { pricingType, fixedPrice, discountPercentage } = this.state;
    if (pricingType === 'fixed') return parseFloat(fixedPrice) > 0;
    if (pricingType === 'percentage') {
      const p = parseFloat(discountPercentage);
      return p >= 1 && p <= 100;
    }
    return false;
  };

  setPricingType = (type) => {
    this.setState({
      pricingType: type,
      fixedPrice: '',
      discountPercentage: '',
      messageError: '',
    }, () => {
      this.calculateSuggestedPrices();
    });
  };

  calcNewPrice = (product) => {
    const { pricingType, discountPercentage } = this.state;
    if (pricingType === 'percentage') {
      if (!product.hasPrice) return null;
      const pct = parseFloat(discountPercentage) || 0;
      return product.originalPrice * (1 - pct / 100);
    }
    if (pricingType === 'fixed') {
      return product.currentUnitPrice > 0 ? product.currentUnitPrice : null;
    }
    return null;
  };

  calculateSuggestedPrices = () => {
    const {
      products, pricingType, fixedPrice, discountPercentage,
    } = this.state;

    if (products.length === 0 || !pricingType) {
      this.setState({
        suggestedPrices: {},
        roundingWarning: false,
        roundingDifference: 0,
      });
      return;
    }

    const totalOriginal = products.reduce(
      (sum, p) => sum + Number(p.originalPrice) * Number(p.qty),
      0,
    );

    if (totalOriginal === 0) return;

    let targetTotal;
    if (pricingType === 'fixed') {
      targetTotal = parseFloat(fixedPrice) || 0;
    } else {
      const discFactor = (100 - parseFloat(discountPercentage || 0)) / 100;
      targetTotal = totalOriginal * discFactor;
    }

    const factor = targetTotal / totalOriginal;
    const suggestedPrices = {};
    let suggestedTotal = 0;

    products.forEach((p) => {
      const suggestedUnitPrice = Number(p.originalPrice) * factor;
      suggestedPrices[p.id] = Math.round(suggestedUnitPrice);
      suggestedTotal += suggestedPrices[p.id] * p.qty;
    });

    const roundingDifference = Math.round((targetTotal - suggestedTotal) * 100) / 100;
    const roundingWarning = Math.abs(roundingDifference) > 0.01;

    this.setState({
      suggestedPrices,
      roundingWarning,
      roundingDifference,
    });
  };

  updateAdjustedPrice = (productId, newPrice) => {
    this.setState(prev => ({
      productAdjustedPrices: {
        ...prev.productAdjustedPrices,
        [productId]: this.sanitizeCurrencyInputValue(newPrice),
      },
    }));
  };

  formatAdjustedPrice = (productId) => {
    this.setState(prev => ({
      productAdjustedPrices: {
        ...prev.productAdjustedPrices,
        [productId]: this.formatCurrencyInputValue(prev.productAdjustedPrices[productId]),
      },
    }));
  };

  render() {
    const {
      name,
      description,
      dateEffective,
      dateExpiration,
      status,
      editPack,
      messageError,
      messageSuccess,
      isLoading,
      products,
      searchValue,
      searchResults,
      showDropdown,
      pricingType,
      fixedPrice,
      discountPercentage,
      imageFile,
      imagePreview,
    } = this.state;

    const pricingValid = this.isPricingValid();
    const totalOriginal = products.reduce(
      (sum, p) => sum + p.originalPrice * p.qty,
      0,
    );
    const totalCurrent = products.reduce(
      (sum, product) => sum + this.getFinalUnitPrice(product) * product.qty,
      0,
    );

    const toDisplay = (iso) => {
      if (!iso) return '';
      const [year, month, day] = iso.split('-');
      return `${day}-${month}-${year}`;
    };

    const today = new Date();
    const baseDate = dateEffective ? new Date(dateEffective) : today;
    const minExpiration = new Date(baseDate);
    minExpiration.setDate(minExpiration.getDate() + 1);

    const saveDisabled = isLoading
      || !pricingValid
      || products.length === 0;

    return (
      <div className="form-main">
        <style>
          {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
        </style>
        <h2 className="header-form-client">{I18n.t('packsRegistration')}</h2>
        <form>
          <div className="form-group">
            <p className="margin-bottom-2">{I18n.t('packImage')}</p>
            {imagePreview && (
              <div style={{ marginBottom: '8px' }}>
                <img
                  src={imageFile ? URL.createObjectURL(imageFile) : imagePreview}
                  alt="pack"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '160px',
                    objectFit: 'contain',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                  }}
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="form-control-file"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                if (file) {
                  this.setState({ imageFile: file, imagePreview: URL.createObjectURL(file) });
                }
              }}
            />
          </div>

          <div className="form-group">
            <p className="margin-bottom-2">{I18n.t('name')}</p>
            <input
              type="text"
              className="form-control"
              placeholder=""
              value={name}
              onChange={e => this.setValue('name', e.target.value)}
              maxLength={fields.max}
            />
          </div>

          <div className="form-group">
            <p className="margin-bottom-2">{I18n.t('description')}</p>
            <textarea
              className="form-control"
              rows={3}
              maxLength={500}
              value={description}
              onChange={e => this.setState({ description: e.target.value })}
            />
          </div>

          <div className="form-group">
            <p className="margin-bottom-2">{I18n.t('dateEffective')}</p>
            <ModernDatepicker
              date={toDisplay(dateEffective)}
              format="DD-MM-YYYY"
              showBorder
              onChange={date => this.setDate('dateEffective', date)}
              placeholder={I18n.t('dateEffective')}
              className="form-control"
              minDate={editPack ? null : today}
            />
          </div>

          <div className="form-group">
            <p className="margin-bottom-2">{I18n.t('dateExpiration')}</p>
            <ModernDatepicker
              date={toDisplay(dateExpiration)}
              format="DD-MM-YYYY"
              showBorder
              onChange={date => this.setDate('dateExpiration', date)}
              placeholder={I18n.t('dateExpiration')}
              className="form-control"
              minDate={editPack ? null : minExpiration}
            />
          </div>

          <div className="form-group">
            <p className="margin-bottom-2">{I18n.t('status')}</p>
            <select
              className="custom-select"
              value={status}
              onChange={e => this.setValue('status', e.target.value)}
            >
              <option value="" disabled>
                {I18n.t('selectStatus')}
              </option>
              <option value="active">{I18n.t('active2')}</option>
              <option value="inactive">{I18n.t('inactive')}</option>
            </select>
          </div>

          {/* FORMA DE PRECIFICAÇÃO */}
          <div className="form-group">
            <h5
              style={{
                borderBottom: '1px solid #dee2e6',
                paddingBottom: '8px',
                marginBottom: '16px',
              }}
            >
              {I18n.t('pricingMethod')}
            </h5>
            <div style={{ display: 'flex', gap: '24px', marginBottom: '12px' }}>
              <div className="form-check">
                <input
                  type="radio"
                  className="form-check-input"
                  id="pricingFixed"
                  name="pricingType"
                  value="fixed"
                  checked={pricingType === 'fixed'}
                  onChange={() => this.setPricingType('fixed')}
                />
                <label className="form-check-label" htmlFor="pricingFixed">
                  {I18n.t('fixedPriceLabel')}
                </label>
              </div>
              <div className="form-check">
                <input
                  type="radio"
                  className="form-check-input"
                  id="pricingpercentage"
                  name="pricingType"
                  value="percentage"
                  checked={pricingType === 'percentage'}
                  onChange={() => this.setPricingType('percentage')}
                />
                <label className="form-check-label" htmlFor="pricingpercentage">
                  {I18n.t('discountPercentageLabel')}
                </label>
              </div>
            </div>

            {pricingType === 'fixed' && (
              <div className="form-group">
                <p className="margin-bottom-2">{I18n.t('finalPackValue')}</p>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  className="form-control"
                  style={{ maxWidth: '200px' }}
                  value={fixedPrice}
                  onChange={e => this.setState({
                    fixedPrice: e.target.value,
                    messageError: '',
                  }, () => this.calculateSuggestedPrices())
                  }
                />
              </div>
            )}

            {pricingType === 'percentage' && (
              <div className="form-group">
                <p className="margin-bottom-2">
                  {I18n.t('percentageDiscountLabel')}
                </p>
                <input
                  type="number"
                  min="1"
                  max="100"
                  className="form-control"
                  style={{ maxWidth: '200px' }}
                  value={discountPercentage}
                  onChange={e => this.setState({
                    discountPercentage: e.target.value,
                    messageError: '',
                  }, () => this.calculateSuggestedPrices())
                  }
                />
                {discountPercentage
                  && (parseFloat(discountPercentage) < 1
                    || parseFloat(discountPercentage) > 100) && (
                    <p
                      className="color-red margin-top-5"
                      style={{ fontSize: '0.85em' }}
                    >
                      {I18n.t('invaliddiscountPercentage')}
                    </p>
                )}
              </div>
            )}
          </div>

          {/* BUSCA DE PRODUTOS */}
          <div className="form-group">
            <p className="margin-bottom-2">{I18n.t('products')}</p>
            {!pricingValid && (
              <p
                style={{
                  fontSize: '0.85em',
                  color: '#ffc107',
                  marginBottom: '6px',
                }}
              >
                <span role="img" aria-label="aviso">⚠️</span>
                {' '}
                {I18n.t('pricingRequired')}
              </p>
            )}
            <div style={{ position: 'relative' }}>
              <input
                ref={this.inputRef}
                type="text"
                className="form-control"
                placeholder={I18n.t('searchProduct')}
                value={searchValue}
                autoComplete="off"
                onChange={e => this.setOnChange(e.target.value)}
                onBlur={() => setTimeout(() => this.setState({ showDropdown: false }), 200)
                }
              />
              {this.state.isLoadingProducts && (
                <div
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid #f3f3f3',
                      borderTop: '2px solid #3498db',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                </div>
              )}
            </div>
            {(showDropdown && searchResults.length > 0)
              && (() => {
                const rect = this.inputRef.current
                  && this.inputRef.current.getBoundingClientRect();
                if (!rect) return null;
                return ReactDOM.createPortal(
                  <div
                    style={{
                      position: 'fixed',
                      top: rect.bottom,
                      left: rect.left,
                      width: rect.width,
                      zIndex: 99999,
                      maxHeight: '260px',
                      overflowY: 'auto',
                      background: '#fff',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    }}
                  >
                    {searchResults.map((item, idx) => (
                      <div
                        key={item.soneparId || idx}
                        role="button"
                        tabIndex="0"
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          background: '#fff',
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          this.selectProduct(item);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f0f7ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff';
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this.selectProduct(item);
                          }
                        }}
                      >
                        <strong>{item.code}</strong>
                        {item.description ? ` - ${item.description}` : ''}
                      </div>
                    ))}
                  </div>,
                  document.body,
                );
              })()}
          </div>

          {/* TABELA DE PRODUTOS */}
          {products.length > 0 && (
            <div className="form-group">
              <p className="margin-bottom-2">{I18n.t('selectedProducts')}</p>
              <table
                className="table table-bordered table-sm"
                style={{ marginTop: '8px' }}
              >
                <thead className="thead-light">
                  <tr>
                    <th>{I18n.t('product')}</th>
                    <th style={{ width: '85px' }}>{I18n.t('quantity')}</th>
                    <th style={{ width: '110px', textAlign: 'right' }}>
                      {I18n.t('originalPrice')}
                    </th>
                    <th style={{ width: '110px', textAlign: 'right' }}>
                      P. Sugerido
                    </th>
                    <th style={{ width: '110px', textAlign: 'right', backgroundColor: '#fffacd' }}>
                      P. Ajustado (editar)
                    </th>
                    <th style={{ width: '100px', textAlign: 'right' }}>
                      Subtotal
                    </th>
                    <th style={{ width: '70px', textAlign: 'right' }}>
                      Diferença
                    </th>
                    <th style={{ width: '60px', textAlign: 'center' }}>
                      {I18n.t('remove')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, idx) => {
                    const suggestedPrice = this.getSuggestedUnitPrice(product.id);
                    const finalPrice = this.getFinalUnitPrice(product);
                    const subtotal = finalPrice * product.qty;
                    const difference = (finalPrice - product.originalPrice) * product.qty;

                    return (
                      <tr key={product.id || idx}>
                        <td>{`${product.code} - ${product.name}`}</td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            value={product.qty}
                            onChange={e => this.updateQty(product.id, e.target.value)}
                            className="form-control form-control-sm"
                          />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {product.hasPrice ? (
                            NumbersHelper.toCurrency(Math.round(product.originalPrice * 100) / 100)
                          ) : (
                            <span style={{ color: '#dc3545' }}>N/A</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {suggestedPrice > 0
                            ? NumbersHelper.toCurrency(Math.round(suggestedPrice * 100) / 100)
                            : '\u2014'}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#fffacd' }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={this.state.productAdjustedPrices[product.id] !== undefined
                              ? this.state.productAdjustedPrices[product.id]
                              : ''}
                            placeholder={suggestedPrice > 0 ? NumbersHelper.toCurrency(suggestedPrice) : ''}
                            onChange={e => this.updateAdjustedPrice(product.id, e.target.value)}
                            onBlur={() => this.formatAdjustedPrice(product.id)}
                            className="form-control form-control-sm"
                            maxLength={fields.float_max}
                            style={{ textAlign: 'right' }}
                          />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {subtotal > 0
                            ? NumbersHelper.toCurrency(Math.round(subtotal * 100) / 100)
                            : '\u2014'}
                        </td>
                        <td style={{ textAlign: 'right', color: difference > 0 ? '#28a745' : '#dc3545' }}>
                          {difference !== 0
                            ? NumbersHelper.toCurrency(Math.round(difference * 100) / 100)
                            : '\u2014'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-link color-red"
                            style={{ padding: 0 }}
                            onClick={() => this.setState({
                              products: products.filter(p => p.id !== product.id),
                            }, () => this.calculateSuggestedPrices())
                            }
                          >
                            {I18n.t('remove')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {this.state.roundingWarning && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '10px 12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404',
                    fontSize: '0.9em',
                  }}
                >
                  <span role="img" aria-label="aviso">⚠️</span>
                  {' '}
                  <strong>Aviso de arredondamento:</strong>
                  {' '}
                  A diferença de
                  {' '}
                  {' '}
                  {NumbersHelper.toCurrency(this.state.roundingDifference)}
                  {' '}
                  será ajustada automaticamente no cálculo final do pack.
                </div>
              )}

              {/* TOTAIS */}
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 16px',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '4px',
                  }}
                >
                  <strong>
                    {I18n.t('totalOriginal')}
                    {':'}
                  </strong>
                  <span>
                    {NumbersHelper.toCurrency(Math.round(totalOriginal * 100) / 100)}
                  </span>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <strong>
                    {I18n.t('totalCurrent')}
                    {':'}
                  </strong>
                  <span
                    style={{
                      color:
                        totalCurrent < totalOriginal || totalOriginal === 0
                          ? '#28a745'
                          : '#dc3545',
                      fontWeight: 'bold',
                    }}
                  >
                    {NumbersHelper.toCurrency(Math.round(totalCurrent * 100) / 100)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {!!messageSuccess && (
            <p className="color-green margin-top-10">{messageSuccess}</p>
          )}
          {!!messageError && (
            <p className="color-red margin-top-10">{messageError}</p>
          )}

          <div className="row margin-left-0 margin-top-20">
            <button
              type="button"
              onClick={() => this.goBack()}
              className="btn btn-warning margin-right-15"
            >
              {I18n.t('return')}
            </button>

            <button
              type="button"
              className="btn btn-dark"
              onClick={() => (!editPack ? this.createPack() : this.updatePack())
              }
              disabled={saveDisabled}
            >
              {!editPack ? I18n.t('toSend') : I18n.t('resend')}
            </button>
          </div>
        </form>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  createPacks: newPack => dispatch(createPack(newPack)),
  updatePacks: updPack => dispatch(updatePack(updPack)),
  uploadImage: (file, packId) => dispatch(uploadPackImage(file, packId)),
});

const mapStateToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(PackForm);
